import { useEffect } from 'react';
import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Editor as IEditor} from "tinymce";

import { BasicTinyMce } from 'modules/common/components/basicTinyMce';
import { GifPicker } from 'modules/common/components/gifPicker/gifPicker';
import * as actions from 'modules/posts/actionCreator';
import { GlobalApplicationState } from 'globalApplicationState';
import { addGif, getInsertedGifs, handleTinyText } from 'utils/textEditorUtils';
import { useIsEditorEmpty } from 'utils/useIsEditorEmpty';
import TextboxButtons from './textboxButtons';

interface ComponentProps {
    placeholder: string
    postId: string
    preview?: boolean;
}

const MentionTextbox: React.FunctionComponent<PropsWithRedux> = ({
    placeholder,
    commentMaxLength,
    savingComment,
    postId,
    preview = false,
    addComment,
}) => {
    const [text, setText] = React.useState("");
    const [letterCount, setCount] = React.useState(0);
    const [bodyPlaintext, setBodyPlaintext] = React.useState("");
    const [gifsVisible, setGifsVisible] = React.useState(false);
    const textRef = React.useRef(null)
    const editorRef = React.useRef<IEditor | null>(null);
    const gifRef = React.useRef<any>(null);

    const isOnlyWhitespace = useIsEditorEmpty(bodyPlaintext, editorRef);

    let commentCooldown = false;
    let timeout: NodeJS.Timeout | undefined = undefined;

    useEffect(() => {
        return () => {
            if (timeout)
                clearTimeout(timeout);
        }
    }, [timeout]);

    const onAdd = () => {
        if (!editorRef.current) return;

        commentCooldown = true;
        timeout = setTimeout(() => { commentCooldown = false }, 1000);

        let plain = bodyPlaintext;
        let content = editorRef.current.getContent();
        let hasGif = getInsertedGifs(content);

        if (hasGif && hasGif.length > 0 && bodyPlaintext === "") {
            plain = "📷 GIF";
        }

        addComment(postId, text, plain).then((response) => {
            if (response) {
                setText('')
                setBodyPlaintext('')
                setCount(0)
            }
        });
    }

    return (
        <div className="mentions-formatting">
            {gifsVisible ?
                <div className="gif-picker-popover">
                    <GifPicker
                        onSelect={(gif) => addGif(gif, setBodyPlaintext, setGifsVisible, editorRef, gifRef)}
                        searchPlaceholder="Search GIFs"
                    />
                </div>
                : null}
            <div className="tiny-text-holder" ref={textRef}>
                <BasicTinyMce
                    isGifsVisible={gifsVisible}
                    onChangeIsGifsVisible={(value: boolean) => setGifsVisible(value)}
                    placeholder={placeholder}
                    editorRef={editorRef}
                    gifRef={gifRef}
                    value={text}
                    id={'tiny-comment-editor'}
                    onChange={() => handleTinyText(setText, setBodyPlaintext, setCount, editorRef.current!)}
                    disabled={preview || savingComment || commentCooldown}
                />
            </div>
            <TextboxButtons 
                onAdd={onAdd}
                onAddDisabled={preview || savingComment || commentCooldown || letterCount === 0 || letterCount > commentMaxLength || isOnlyWhitespace || gifsVisible}
                letterCount={letterCount}
                maxLength={commentMaxLength}
            />
        </div>
    )
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        commentMaxLength: state.settings.clientSettings.commentSystem.commentMaxLength,
        savingComment: state.posts.postView.savingComment,
    }),
    {
        addComment: actions.addComment,
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;
export default connector(MentionTextbox);
